/**
 * One owner of the Twilio Device across tabs. `twilioDeviceLock` only excludes the two
 * pipelines inside a tab — it is module state, so tabs cannot see each other, and each
 * Device registers the same identity for the gateway to ring.
 *
 * The protocol is a veto rather than a lease: only a tab with a call answers a claim, so
 * silence means "safe to take" and a crashed or frozen owner needs no timeout (Chrome will
 * not freeze a tab holding live WebRTC). Ownership otherwise goes to the newcomer, as it
 * already does in-tab; a call is the exception and needs `force`. Every failure grants
 * ownership, so this can never be what stops a rep dialling.
 */

const CHANNEL_NAME = 'levitate.aida.dialer';

/**
 * Identity the Device belongs to, used to scope the channel. Impersonation makes one
 * browser hold two identities that may each legitimately register, so tabs must only
 * coordinate with tabs that would contend for the same Twilio registration.
 */
export function dialerScopeFor(accountId: string | undefined, userId: string | undefined): string {
	return `${accountId ?? 'no-account'}.${userId ?? 'no-user'}`;
}

/**
 * How long a claim waits for a veto. Covers a BroadcastChannel round trip between
 * live tabs with room to spare; the cost of it being too short is a taken call, so it
 * is deliberately far longer than the delivery it is waiting on.
 */
const VETO_WINDOW_MS = 250;

type HelloMessage = { type: 'hello' };
type ClaimMessage = { type: 'claim'; tabId: string; nonce: string };
type VetoMessage = { type: 'veto'; nonce: string };
type OwnerMessage = { type: 'owner'; tabId: string; seq: number; busy: boolean };
type ReleaseMessage = { type: 'release'; tabId: string };
type CoordinatorMessage = HelloMessage | ClaimMessage | VetoMessage | OwnerMessage | ReleaseMessage;

export type DialerTabStatus = {
	/** This tab holds the Device. */
	isOwner: boolean;
	/** Another tab holds it — this tab must not build a Device until that one yields. */
	otherTabOwns: boolean;
	/** That tab is on a call, so taking the Device from it needs the rep to confirm. */
	otherTabBusy: boolean;
};

export const NoOwnership: DialerTabStatus = { isOwner: false, otherTabOwns: false, otherTabBusy: false };

export type DialerTabOwner = {
	/** Whether the rep is on a call here, which vetoes another tab's claim. */
	isOnCall: () => boolean;
	/** Tear down this tab's Device — another tab has taken ownership. */
	onEvicted: () => void;
};

export type DialerTabCoordinator = {
	readonly tabId: string;
	/** Resolves true when this tab may build a Device. `force` ignores a veto. */
	claim: (owner: DialerTabOwner, options?: { force?: boolean }) => Promise<boolean>;
	release: () => void;
	/** Re-announce, so other tabs learn this tab has started or finished a call. */
	refreshBusy: () => void;
	subscribe: (listener: (status: DialerTabStatus) => void) => () => void;
	status: () => DialerTabStatus;
	dispose: () => void;
};

type PendingClaim = { resolve: (vetoed: boolean) => void; timer: ReturnType<typeof setTimeout> };

type CoordinatorOptions = {
	channelName?: string;
	vetoWindowMs?: number;
};

/**
 * Guards the ordering arithmetic: a payload without a numeric `seq` would make `seenSeq`
 * NaN, after which no comparison is ever true and the tab re-announces instead of
 * yielding. Cross-version tabs are the realistic source — a long-lived SPA survives
 * deploys, so a future protocol change must degrade to "ignored", not "wedged".
 */
function isCoordinatorMessage(data: unknown): data is CoordinatorMessage {
	if (!data || typeof data !== 'object') return false;
	const message = data as { type?: unknown; tabId?: unknown; nonce?: unknown; seq?: unknown; busy?: unknown };
	switch (message.type) {
		case 'hello':
			return true;
		case 'claim':
			return typeof message.tabId === 'string' && typeof message.nonce === 'string';
		case 'veto':
			return typeof message.nonce === 'string';
		case 'owner':
			return typeof message.tabId === 'string' && Number.isFinite(message.seq) && typeof message.busy === 'boolean';
		case 'release':
			return typeof message.tabId === 'string';
		default:
			return false;
	}
}

const makeId = (): string => {
	try {
		return crypto.randomUUID();
	} catch {
		// randomUUID needs a secure context in some browsers; only uniqueness matters here.
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	}
};

export function createDialerTabCoordinator(options: CoordinatorOptions = {}): DialerTabCoordinator {
	const channelName = options.channelName ?? CHANNEL_NAME;
	const vetoWindowMs = options.vetoWindowMs ?? VETO_WINDOW_MS;
	const tabId = makeId();

	const listeners = new Set<(status: DialerTabStatus) => void>();
	const pendingClaims = new Map<string, PendingClaim>();

	let channel: BroadcastChannel | null | undefined;
	let owner: DialerTabOwner | null = null;
	let isOwner = false;
	/**
	 * Ownership order. A claim takes `seenSeq + 1`, so the later claim always carries the
	 * higher number and the earlier owner stands down — a wall clock cannot do this job,
	 * because two claims land in the same millisecond and would tie by accident.
	 */
	let seenSeq = 0;
	let mySeq = 0;
	let otherTabOwns = false;
	let otherTabBusy = false;
	let disposed = false;

	const status = (): DialerTabStatus => ({ isOwner, otherTabOwns, otherTabBusy });

	const notify = () => {
		const next = status();
		listeners.forEach(listener => {
			try {
				listener(next);
			} catch (err) {
				console.error('[dialerTabCoordinator] status listener threw', err);
			}
		});
	};

	const post = (message: CoordinatorMessage) => {
		try {
			getChannel()?.postMessage(message);
		} catch (err) {
			console.error('[dialerTabCoordinator] failed to post', message.type, err);
		}
	};

	const settleClaim = (nonce: string, vetoed: boolean) => {
		const pending = pendingClaims.get(nonce);
		if (!pending) return;
		clearTimeout(pending.timer);
		pendingClaims.delete(nonce);
		pending.resolve(vetoed);
	};

	const setOwner = (next: boolean) => {
		isOwner = next;
		if (next) {
			seenSeq += 1;
			mySeq = seenSeq;
			otherTabOwns = false;
			otherTabBusy = false;
		}
		notify();
	};

	const announceOwnership = () => post({ type: 'owner', tabId, seq: mySeq, busy: !!owner?.isOnCall() });

	const evictLocal = () => {
		const evicting = owner;
		owner = null;
		isOwner = false;
		try {
			evicting?.onEvicted();
		} catch (err) {
			console.error('[dialerTabCoordinator] evictor threw', err);
		}
	};

	const handleMessage = (event: MessageEvent<CoordinatorMessage>) => {
		const data = event.data;
		if (disposed || !isCoordinatorMessage(data)) return;

		switch (data.type) {
			case 'hello':
				// A tab just opened. Owners answer so it can render that the dialer is live
				// elsewhere, and so its own later claim outranks ours instead of tying.
				if (isOwner) announceOwnership();
				return;

			case 'claim':
				if (!isOwner) return;
				// Answer with where the sequence stands, so the claimant's own claim outranks
				// ours and we are the one that stands down. A tab that has never heard from us
				// would otherwise claim a lower number and evict itself moments later.
				announceOwnership();
				// Only an owner can be on a call, so only an owner has grounds to veto.
				if (owner?.isOnCall()) post({ type: 'veto', nonce: data.nonce });
				return;

			case 'veto':
				settleClaim(data.nonce, true);
				return;

			case 'owner': {
				if (data.tabId === tabId) return;
				seenSeq = Math.max(seenSeq, data.seq);
				if (isOwner) {
					// Equal numbers mean both tabs claimed without having heard from each other,
					// and the id settles it so exactly one yields — untied, each would honour the
					// other's announcement and both would tear down.
					const yieldToThem = data.seq > mySeq || (data.seq === mySeq && data.tabId > tabId);
					if (!yieldToThem) {
						// Ours is the later claim; re-assert so the stale owner stands down.
						announceOwnership();
						return;
					}
					evictLocal();
				}
				otherTabOwns = true;
				otherTabBusy = data.busy;
				notify();
				return;
			}

			case 'release':
				if (data.tabId === tabId) return;
				otherTabOwns = false;
				otherTabBusy = false;
				notify();
				return;

			default:
				return;
		}
	};

	const handlePageHide = () => {
		if (isOwner) post({ type: 'release', tabId });
	};

	function getChannel(): BroadcastChannel | null {
		if (channel !== undefined) return channel;
		if (typeof BroadcastChannel === 'undefined') {
			channel = null;
			return channel;
		}
		try {
			const created = new BroadcastChannel(channelName);
			created.onmessage = handleMessage;
			channel = created;
			// `pagehide` rather than `beforeunload`: it fires for the back/forward cache too,
			// and a tab that never announces its exit leaves the others showing it as owner.
			window.addEventListener('pagehide', handlePageHide);
		} catch (err) {
			console.error('[dialerTabCoordinator] BroadcastChannel unavailable', err);
			channel = null;
		}
		return channel;
	}

	const claim = async (nextOwner: DialerTabOwner, claimOptions?: { force?: boolean }): Promise<boolean> => {
		// A retired coordinator refuses rather than failing open. Its caller is stale — the
		// app has moved to another identity — and granting would leave a Device registered
		// that no live coordinator knows about, the one outcome this module must prevent.
		if (disposed) return false;

		owner = nextOwner;

		// Already ours — refresh the callbacks and skip the round trip, the way a same-owner
		// re-claim on `twilioDeviceLock` is a no-op. Token re-inits run through here and must
		// not pay the veto window, nor risk losing the Device.
		if (isOwner) return true;

		// No channel means no way to coordinate; granting keeps a single tab working.
		if (!getChannel()) {
			setOwner(true);
			return true;
		}

		// Forced claims still make the round trip. They ignore the veto, but they need the
		// answering owner's ownership number, or a takeover would tie with the tab it is
		// taking from and could lose the tiebreak.
		const nonce = makeId();
		const vetoed = await new Promise<boolean>(resolve => {
			const timer = setTimeout(() => settleClaim(nonce, false), vetoWindowMs);
			pendingClaims.set(nonce, { resolve, timer });
			post({ type: 'claim', tabId, nonce });
		});

		if (disposed) return false;

		if (vetoed && !claimOptions?.force) {
			owner = null;
			notify();
			return false;
		}

		setOwner(true);
		announceOwnership();
		return true;
	};

	const release = () => {
		owner = null;
		if (disposed || !isOwner) return;
		setOwner(false);
		post({ type: 'release', tabId });
	};

	const refreshBusy = () => {
		if (disposed || !isOwner) return;
		announceOwnership();
	};

	const subscribe = (listener: (next: DialerTabStatus) => void) => {
		if (disposed) return (): void => {};
		listeners.add(listener);
		listener(status());
		return () => {
			listeners.delete(listener);
		};
	};

	const dispose = () => {
		if (disposed) return;
		// Tell the other tabs before going quiet, or they keep showing a retired tab as owner.
		if (isOwner) post({ type: 'release', tabId });
		disposed = true;
		// Settle what is in flight, or a caller awaiting a claim never resumes: the softphone
		// caches that promise, so every later init would return one that can no longer settle.
		pendingClaims.forEach(pending => {
			clearTimeout(pending.timer);
			pending.resolve(false);
		});
		pendingClaims.clear();
		listeners.clear();
		owner = null;
		isOwner = false;
		otherTabOwns = false;
		otherTabBusy = false;
		if (channel) {
			window.removeEventListener('pagehide', handlePageHide);
			channel.close();
		}
		// `null`, not `undefined`: the latter is `getChannel`'s "not built yet", so a retired
		// coordinator would reopen a channel whose handler is disabled — deaf to every veto,
		// and so certain to grant itself ownership and never yield.
		channel = null;
	};

	// Opened up front, not on first claim: a tab that never claims still has to hear who
	// owns the Device so it can show that the dialer is live somewhere else.
	if (getChannel()) post({ type: 'hello' });

	return { tabId, claim, release, refreshBusy, subscribe, status, dispose };
}

let shared: { scope: string; coordinator: DialerTabCoordinator } | undefined;

/**
 * The coordinator for one identity, from `dialerScopeFor`. A changed scope means the
 * session now belongs to someone else, so the previous channel is closed rather than
 * left to arbitrate for a registration it no longer shares.
 */
export function dialerTabCoordinator(scope: string): DialerTabCoordinator {
	if (shared?.scope === scope) return shared.coordinator;
	shared?.coordinator.dispose();
	shared = {
		scope,
		coordinator: createDialerTabCoordinator({ channelName: `${CHANNEL_NAME}.${scope}` }),
	};
	return shared.coordinator;
}
